import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './RegisterAngler.css';
import './Button.css';

const RegisterAngler = () => {
    const [anglers, setAnglers] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showAnglerList, setShowAnglerList] = useState(false);
    const [newAngler, setNewAngler] = useState({
        first_name: '',
        last_name: '',
        license_number: '',
        calcutta: false,
        extra_bait: false,
        business: ''
    });
    const [editingAngler, setEditingAngler] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [successMessage, setSuccessMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [anglersPerPage] = useState(50);
    const [totalAnglers, setTotalAnglers] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    const sortedAnglers = React.useMemo(() => {
        let sortableAnglers = [...anglers];
        if (sortConfig.key !== null) {
            sortableAnglers.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableAnglers;
    }, [anglers, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };


    useEffect(() => {
        if (showAnglerList) {
            fetchAnglers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAnglerList, currentPage, searchTerm]);

    const fetchAnglers = async () => {
        try {
            const response = await api.get('/anglers/', {
                params: {
                    skip: (currentPage - 1) * anglersPerPage,
                    limit: anglersPerPage,
                    search: searchTerm
                }
            });
            setAnglers(response.data.anglers);
            setTotalAnglers(response.data.total);
        } catch (error) {
            console.error('Error fetching anglers:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewAngler(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleEditInputChange = (e, anglerId) => {
        const { name, value, type, checked } = e.target;
        setAnglers(prevAnglers => prevAnglers.map(angler => {
            if (angler.id === anglerId) {
                return { ...angler, [name]: type === 'checkbox' ? checked : value };
            }
            return angler;
        }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await api.post('/anglers/', newAngler);
            setSuccessMessage(`Successfully added ${response.data.first_name} ${response.data.last_name}`);
            setNewAngler({
                first_name: '',
                last_name: '',
                license_number: '',
                calcutta: false,
                extra_bait: false,
                business: ''
            });
            // Clear the success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
            if (showAnglerList) {
                fetchAnglers();
            }
        } catch (error) {
            console.error('Error creating angler:', error);
        }
    };

    const handleUpdate = async (anglerId) => {
        const anglerToUpdate = anglers.find(angler => angler.id === anglerId);
        try {
            await api.put(`/anglers/${anglerId}`, anglerToUpdate);
            setEditingAngler(null);
            fetchAnglers();
        } catch (error) {
            console.error('Error updating angler:', error);
        }
    };

    const handleFileImport = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const formData = new FormData();
            formData.append('file', file);
            try {
                const response = await api.post('/anglers/import', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                setSuccessMessage(response.data.message);
                setTimeout(() => setSuccessMessage(''), 3000);
                if (showAnglerList) {
                    fetchAnglers();
                }
            } catch (error) {
                console.error('Error importing anglers:', error);
            }
        }
    };

    return (
        <div className="register-angler-container">
            <h2>Register Angler</h2>
            <div className="action-buttons">
                <button className="btn btn-primary" onClick={() => { setShowAddForm(!showAddForm); setShowAnglerList(false); }}>Add Angler</button>
                <button className="btn btn-primary" onClick={() => { setShowAnglerList(!showAnglerList); setShowAddForm(false); }}>View All Anglers</button>
                <label htmlFor="import-csv" className="btn btn-secondary">
                    Import Anglers (CSV)
                    <input type="file" id="import-csv" className="hidden-file-input" onChange={handleFileImport} accept=".csv" />
                </label>
            </div>

            {showAddForm && (
                <div className="add-angler-form">
                    <div className="form-card-header">Add New Angler</div>
                    {successMessage && <div className="success-message">{successMessage}</div>}
                    <form onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="first_name">First Name <span className="required">*</span></label>
                                <input id="first_name" name="first_name" value={newAngler.first_name} onChange={handleInputChange} placeholder="Enter first name" required />
                            </div>
                            <div className="form-group">
                                <label htmlFor="last_name">Last Name <span className="required">*</span></label>
                                <input id="last_name" name="last_name" value={newAngler.last_name} onChange={handleInputChange} placeholder="Enter last name" required />
                            </div>
                        </div>
                        <div className="form-group">
                            <label htmlFor="license_number">License Number <span className="required">*</span></label>
                            <input id="license_number" name="license_number" value={newAngler.license_number} onChange={handleInputChange} placeholder="Enter license number" required />
                        </div>
                        <div className="form-group">
                            <label htmlFor="business">Business / Vessel</label>
                            <input id="business" name="business" value={newAngler.business} onChange={handleInputChange} placeholder="Optional" />
                        </div>
                        <div className="checkbox-group">
                            <label className="checkbox-label">
                                <input type="checkbox" name="calcutta" checked={newAngler.calcutta} onChange={handleInputChange} />
                                <span className="checkbox-track"><span className="checkbox-thumb" /></span>
                                Calcutta
                            </label>
                            <label className="checkbox-label">
                                <input type="checkbox" name="extra_bait" checked={newAngler.extra_bait} onChange={handleInputChange} />
                                <span className="checkbox-track"><span className="checkbox-thumb" /></span>
                                Extra Bait
                            </label>
                        </div>
                        <button className="btn btn-success submit-btn" type="submit">Save and Add Next</button>
                    </form>
                </div>
            )}

            {showAnglerList && (
                <div className="angler-list">
                    <h3>All Registered Anglers ({totalAnglers})</h3>
                    <div className="angler-list-body">
                    <input
                        type="text"
                        placeholder="Search anglers..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="search-input"
                    />
                    <table>
                        <thead>
                            <tr>
                                <th className="sortable-header" onClick={() => requestSort('first_name')}>First Name</th>
                                <th className="sortable-header" onClick={() => requestSort('last_name')}>Last Name</th>
                                <th className="sortable-header" onClick={() => requestSort('license_number')}>License Number</th>
                                <th className="sortable-header" onClick={() => requestSort('calcutta')}>Calcutta</th>
                                <th className="sortable-header" onClick={() => requestSort('extra_bait')}>Extra Bait</th>
                                <th className="sortable-header" onClick={() => requestSort('business')}>Business</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedAnglers.map(angler => (
                                <tr key={angler.id}>
                                    {editingAngler === angler.id ? (
                                        <>
                                            <td><input name="first_name" value={angler.first_name} onChange={(e) => handleEditInputChange(e, angler.id)} /></td>
                                            <td><input name="last_name" value={angler.last_name} onChange={(e) => handleEditInputChange(e, angler.id)} /></td>
                                            <td><input name="license_number" value={angler.license_number} onChange={(e) => handleEditInputChange(e, angler.id)} /></td>
                                            <td><input type="checkbox" name="calcutta" checked={angler.calcutta} onChange={(e) => handleEditInputChange(e, angler.id)} /></td>
                                            <td><input type="checkbox" name="extra_bait" checked={angler.extra_bait} onChange={(e) => handleEditInputChange(e, angler.id)} /></td>
                                            <td><input name="business" value={angler.business} onChange={(e) => handleEditInputChange(e, angler.id)} /></td>
                                            <td className="actions">
                                                <button className="btn btn-success" onClick={() => handleUpdate(angler.id)}>Save</button>
                                                <button className="btn" onClick={() => setEditingAngler(null)}>Cancel</button>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>{angler.first_name}</td>
                                            <td>{angler.last_name}</td>
                                            <td>{angler.license_number}</td>
                                            <td>{angler.calcutta ? 'Yes' : 'No'}</td>
                                            <td>{angler.extra_bait ? 'Yes' : 'No'}</td>
                                            <td>{angler.business}</td>
                                            <td className="actions">
                                                <button className="btn btn-secondary" onClick={() => setEditingAngler(angler.id)}>Edit</button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="pagination">
                        <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                            Previous
                        </button>
                        <span>Page {currentPage} of {Math.ceil(totalAnglers / anglersPerPage)}</span>
                        <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalAnglers / anglersPerPage)))} disabled={currentPage === Math.ceil(totalAnglers / anglersPerPage)}>
                            Next
                        </button>
                    </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegisterAngler;